import { Component, computed, Inject, input, InputSignal, Optional, Signal } from '@angular/core';
import {
  getResizeByProportion,
  ImageSourceSetComponent,
  Source,
} from '@candyland/angular/shared/tt-com-processing/image/feature';
import { select, Store } from '@ngrx/store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthPartialStateV2, AuthStateV2, getAuthStateV2 } from '@candyland/angular/shared/data-access-nmo';
import {
  buildConfigForInfomakerImage,
  buildImageUrl,
  CropsType,
  PossibleCrops,
  ProcessingConfig,
} from '@candyland/generic/shared/tt-com-processing/util';
import { Observable, of, startWith } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { TT_ASSETS_BUCKET_NAME, TT_COM_PROCESSING_ENDPOINT } from '@candyland/angular/shared/util-nmo';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

/**
 * documentation on this component:
 * https://mohomedia.atlassian.net/wiki/spaces/EN/pages/3523248133/infomaker-image-source-set+component
 */

@Component({
  selector: 'lib-infomaker-image-source-set',
  imports: [ImageSourceSetComponent, MatTooltipModule, MatIconModule, ClipboardModule, MatSnackBarModule],
  templateUrl: './infomaker-image-source-set.component.html',
  styleUrls: ['./infomaker-image-source-set.component.scss'],
  standalone: true,
})
export class InfomakerImageSourceSetComponent {
  // gets added by code/libs/angular/shared/article-tt-com/data-access/src/lib/transformer/elements-transformer.ts automatically
  type: InputSignal<'x-im/image'> = input('x-im/image'); // *
  altText: InputSignal<string> = input();
  crops: InputSignal<CropsType> = input();
  uuid: InputSignal<string> = input();
  text: InputSignal<string> = input();
  bylineAuthors: InputSignal<string[]> = input();
  fileName: InputSignal<string> = input(); // *
  priority: InputSignal<boolean> = input(false);
  sizeConfig: InputSignal<Size | Size[]> = input();
  // * not used - just there because it's in the model

  sizesConfig: Signal<Size[]> = computed((): Size[] => {
    const size: Size | Size[] = this.sizeConfig();
    return Array.isArray(size) ? size : [size];
  });

  authState$: Observable<AuthStateV2> = null;

  hasArticlePreviewRights: Signal<boolean> = toSignal<boolean>(
    this.authState$
      ? this.authState$.pipe(
          map((state: AuthStateV2): boolean => state && state.isLoggedIn && state.userData.permissions.PreviewArticle),
          shareReplay({ refCount: true, bufferSize: 1 }),
          startWith(false)
        )
      : of(false)
  );
  uri: Signal<string> = computed((): string => `${this.ttAssetsBucketName}/im-content/images/${this.uuid()}`);

  defaultCrop: Signal<ProcessingConfig> = computed((): ProcessingConfig => {
    const desktopMedia: string = ImageSourceSetComponent.defaultDesktopMedia;
    const sizes: Size[] = this.sizesConfig();
    for (const size of sizes) {
      if (size?.media === desktopMedia) {
        return buildConfigForInfomakerImage(this.crops(), size.crop, size.defaultSize);
      }
    }
    return buildConfigForInfomakerImage(this.crops(), sizes[0].crop, sizes[0].defaultSize);
  });

  sourceConfig: Signal<Source[]> = computed((): Source[] => {
    const sizes: Size[] = this.sizesConfig();
    const res: Source[] = [];
    for (const size of sizes) {
      res.push(this.buildSource(size, this.crops(), this.uri()));
    }
    return res;
  });
  constructor(
    @Inject(TT_COM_PROCESSING_ENDPOINT) private readonly ttComProcessingEndpoint: string,
    @Inject(TT_ASSETS_BUCKET_NAME) private ttAssetsBucketName: string,
    @Optional() private authStore: Store<AuthPartialStateV2>,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar
  ) {
    this.authState$ = this.authStore
      ? this.authStore.pipe(
          select(getAuthStateV2),
          filter(
            (state: AuthStateV2): boolean => !!state && ((state.isLoggedIn && !!state.userData) || !state.isLoggedIn)
          ),
          shareReplay({ refCount: true, bufferSize: 1 })
        )
      : null;
  }

  protected copyImageUrl(event: Event): void {
    event.stopPropagation();
    const imageUrl = `https://${this.ttComProcessingEndpoint}/image/${this.uri()}`;
    const src: string = buildImageUrl(imageUrl, this.defaultCrop());
    this.clipboard.copy(src);
    this.snackBar.open('Bild-URL kopiert!', '', {
      duration: 3500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private buildSource(size: Size, crops: CropsType, uri: string): Source {
    const defaultCrop: ProcessingConfig = buildConfigForInfomakerImage(crops, size.crop, size.defaultSize);
    const source: Source = {
      uri,
      versions: [],
      containerWidth: size.containerWidth,
      media: size.media,
    };
    let defaultAdded = false;
    const multipliers: number[] = size?.multipliers || [0.4, 0.5, 0.75, 1.5, 2];
    for (const multiplier of multipliers) {
      if (multiplier < 1) {
        source.versions.push(getResizeByProportion(defaultCrop, multiplier));
      } else if (multiplier > 1) {
        if (!defaultAdded) {
          source.versions.push(defaultCrop);
          defaultAdded = true;
        }
        source.versions.push(getResizeByProportion(defaultCrop, multiplier));
      }
    }
    if (!defaultAdded) {
      source.versions.push(defaultCrop);
    }
    return source;
  }
}

export interface Size {
  defaultSize?: { width: number; height?: number };
  containerWidth: number;
  crop: PossibleCrops;
  media?: string;
  multipliers?: number[];
}
